import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Seo } from '@/lib/seo';
import { useAuth } from '@/lib/auth';
import { Loader, Award, ArrowRight, Warning } from '@/components/ui/Icons';

type Event = {
  kind: 'audit' | 'login';
  id: number;
  actor: string | null;
  event: string;
  meta: Record<string, unknown> | null;
  quote_id: number | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
};

type Resp = { ok: true; entries: Event[] };

export default function SuperEvents() {
  const { user, loading } = useAuth();
  const { data, isLoading, isError } = useQuery<Resp>({
    queryKey: ['super-events'],
    queryFn: () => api<Resp>('/admin/super/events?limit=80'),
    refetchInterval: 30_000,
    enabled: !!user?.is_super,
  });

  if (loading) return null;
  if (!user?.is_super) return <Navigate to="/admin/quotes" replace />;

  return (
    <>
      <Seo title="Activity feed" description="Super-admin activity feed" path="/admin/super/events" noindex />

      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">
            <Award className="h-3 w-3" /> Super-admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold text-navy-900">Activity feed</h1>
          <p className="mt-1 text-sm text-ink-700">
            All sign-ins and admin actions across the site, newest first. Auto-refreshes every 30 seconds.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to="/admin/super/login-attempts" className="btn-ghost">Login attempts <ArrowRight className="h-3 w-3" /></Link>
          <Link to="/admin/super/audit" className="btn-ghost">Audit log <ArrowRight className="h-3 w-3" /></Link>
        </div>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-2 text-ink-700"><Loader className="h-5 w-5" /> Loading…</div>
      ) : isError || !data ? (
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-700">Couldn&rsquo;t load.</div>
      ) : (
        <ol className="space-y-2">
          {data.entries.map((e) => <EventRow key={`${e.kind}-${e.id}`} e={e} />)}
        </ol>
      )}
    </>
  );
}

function EventRow({ e }: { e: Event }) {
  const isFailure = e.event === 'login_failure';
  const colour =
    isFailure ? 'border-red-200 bg-red-50/40' :
    e.kind === 'login' ? 'border-green-200 bg-green-50/40' :
    'border-paper-100 bg-paper-0';

  return (
    <li className={`flex items-start gap-3 rounded-lg border ${colour} p-3 shadow-xs`}>
      <span className="mt-0.5 grid h-7 w-7 flex-none place-items-center rounded-full bg-paper-50 ring-1 ring-paper-100 text-xs font-semibold text-navy-800">
        {isFailure ? <Warning className="h-3.5 w-3.5 text-red-700" /> : e.kind === 'login' ? '🔓' : '✎'}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-navy-900">
          {prettyEvent(e.event)}
          {e.quote_id ? <Link to={`/admin/quotes/${e.quote_id}`} className="ml-2 text-xs font-normal text-gold-700 underline-offset-2 hover:underline">Quote #{e.quote_id}</Link> : null}
        </p>
        <p className="text-xs text-ink-700">
          {e.actor ?? '—'}
          {e.ip ? ` · ${e.ip}` : ''}
          {' · '}
          {new Date(e.created_at).toLocaleString('en-GB')}
        </p>
        {e.meta && (
          <p className="mt-1 text-xs text-ink-700">
            {Object.entries(e.meta)
              .map(([k, v]) => v == null ? null : `${k}: ${String(v)}`)
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </div>
    </li>
  );
}

function prettyEvent(e: string): string {
  return e.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
